import { useEffect, useState } from 'react'
import { DatePicker } from 'components/date-picker'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet'
import {
  ALL,
  CLIENT_PLATFORM_LABELS,
  clientVersionKey,
  clientVersionLabel,
  FilterState,
  initialFilters,
  LOGIN_METHOD_LABELS,
  PLATFORM_LABELS,
  SEGMENT_LABELS,
  SORT_LABELS,
  SOURCE_LABELS,
} from 'helpers/user-filters'
import { useAppDispatch, useAppSelector } from 'store/store'
import { fetchUserFilterOptions } from 'store/users/usersSlice'

interface UsersFiltersSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  filters: FilterState
  onApply: (filters: FilterState) => void
}

interface OptionSelectProps {
  label: string
  value: string
  allLabel?: string
  options: Record<string, string>
  onChange: (value: string) => void
}

const OptionSelect = ({
  label,
  value,
  allLabel,
  options,
  onChange,
}: OptionSelectProps) => (
  <div className='flex flex-col gap-1.5'>
    <Label>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className='w-full'>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {allLabel && <SelectItem value={ALL}>{allLabel}</SelectItem>}
        {Object.entries(options).map(([key, optionLabel]) => (
          <SelectItem key={key} value={key}>
            {optionLabel}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
)

/**
 * The less frequent user filters (dates, segment, platform, sort), edited as a
 * draft and applied in one go. The Sì/No filters and the search live in the bar.
 */
export const UsersFiltersSheet = ({
  open,
  onOpenChange,
  filters,
  onApply,
}: UsersFiltersSheetProps) => {
  const [draft, setDraft] = useState(filters)
  const dispatch = useAppDispatch()
  const filterOptions = useAppSelector((state) => state.users.filterOptions)

  // The versions in use change with each release: loaded the first time the
  // sheet opens, not with every search
  useEffect(() => {
    if (open && !filterOptions) dispatch(fetchUserFilterOptions())
  }, [open, filterOptions, dispatch])

  // The versions of the picked platform, with how many users have each one
  const versionOptions = Object.fromEntries(
    (filterOptions?.client_versions ?? [])
      .filter(
        ({ platform }) =>
          draft.clientPlatform === ALL || platform === draft.clientPlatform
      )
      .map(({ platform, version, users }) => {
        const key = clientVersionKey(platform, version)
        return [key, `${clientVersionLabel(key)} (${users})`]
      })
  )

  // Reseed the draft when the sheet opens. Adjusting state during render rather
  // than in an effect: https://react.dev/learn/you-might-not-need-an-effect
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setDraft(filters)
  }

  const set = <K extends keyof FilterState>(key: K, value: FilterState[K]) =>
    setDraft((current) => ({ ...current, [key]: value }))

  const apply = (next: FilterState) => {
    onApply(next)
    onOpenChange(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='overflow-y-auto'>
        <SheetHeader>
          <SheetTitle>Altri filtri</SheetTitle>
          <SheetDescription>
            Le date sono inclusive. L&apos;ultimo accesso è l&apos;ultima
            chiamata fatta con una sessione ancora esistente. Client e versione
            sono gli ultimi visti
            {filterOptions?.login_tracked_since &&
              `, tracciati dal ${new Date(
                filterOptions.login_tracked_since
              ).toLocaleDateString('it-IT')}`}
            .
          </SheetDescription>
        </SheetHeader>

        <div className='flex flex-col gap-4 px-4'>
          <div className='flex flex-col gap-1.5'>
            <Label>Data di iscrizione</Label>
            <DatePicker
              selected={draft.createdFrom}
              onSelect={(date) => set('createdFrom', date)}
              placeholder='Dal'
            />
            <DatePicker
              selected={draft.createdTo}
              onSelect={(date) => set('createdTo', date)}
              placeholder='Al'
            />
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label>Ultimo accesso</Label>
            <DatePicker
              selected={draft.lastActiveFrom}
              onSelect={(date) => set('lastActiveFrom', date)}
              placeholder='Dal'
            />
            <DatePicker
              selected={draft.lastActiveTo}
              onSelect={(date) => set('lastActiveTo', date)}
              placeholder='Al'
            />
          </div>

          <OptionSelect
            label='Segmento'
            value={draft.segment}
            allLabel='Tutti'
            options={SEGMENT_LABELS}
            onChange={(value) => set('segment', value)}
          />
          <OptionSelect
            label='Piattaforma (token push)'
            value={draft.platform}
            allLabel='Tutte'
            options={PLATFORM_LABELS}
            onChange={(value) => set('platform', value)}
          />
          <OptionSelect
            label='Client (ultimo visto)'
            value={draft.clientPlatform}
            allLabel='Tutti'
            options={CLIENT_PLATFORM_LABELS}
            onChange={(value) =>
              setDraft((current) => ({
                ...current,
                clientPlatform: value,
                clientVersion: ALL,
              }))
            }
          />
          <OptionSelect
            label='Versione app/web'
            value={draft.clientVersion}
            allLabel='Tutte'
            options={versionOptions}
            onChange={(value) => set('clientVersion', value)}
          />
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='client-version-below'>Versione precedente a</Label>
            <Input
              id='client-version-below'
              inputMode='decimal'
              placeholder='es. 3.6.0'
              value={draft.clientVersionBelow}
              onChange={(e) => set('clientVersionBelow', e.target.value)}
            />
          </div>
          <OptionSelect
            label='Ultimo login da'
            value={draft.lastSignInSource}
            allLabel='Tutti'
            options={SOURCE_LABELS}
            onChange={(value) => set('lastSignInSource', value)}
          />
          <OptionSelect
            label='Metodo di login'
            value={draft.loginMethod}
            allLabel='Tutti'
            options={LOGIN_METHOD_LABELS}
            onChange={(value) => set('loginMethod', value)}
          />

          <div className='grid grid-cols-2 gap-2'>
            <OptionSelect
              label='Ordina per'
              value={draft.sort}
              options={SORT_LABELS as Record<string, string>}
              onChange={(value) => set('sort', value as FilterState['sort'])}
            />
            <OptionSelect
              label='Ordine'
              value={draft.direction}
              options={{ desc: 'Decrescente', asc: 'Crescente' }}
              onChange={(value) =>
                set('direction', value as FilterState['direction'])
              }
            />
          </div>
        </div>

        <SheetFooter className='flex-row gap-2'>
          <Button className='flex-1' onClick={() => apply(draft)}>
            Applica
          </Button>
          <Button
            variant='outline'
            onClick={() =>
              // only the filters of this sheet: search and Sì/No stay as they are
              apply({
                ...initialFilters,
                query: draft.query,
                premium: draft.premium,
                fitp: draft.fitp,
                accountConfirmed: draft.accountConfirmed,
                banned: draft.banned,
              })
            }
          >
            Azzera
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
